import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  runTransaction,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

function AdminDeposit() {
  const [deposits, setDeposits] = useState([]);
  const [loadingIds, setLoadingIds] = useState([]);

  useEffect(() => {
    const q = query(
      collection(db, "deposit_requests"),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setDeposits(data);
    });

    return () => unsub();
  }, []);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  // ✅ APPROVE
  const approveDeposit = async (d) => {
    const { id, userId, amount } = d;

    setLoadingIds((prev) => [...prev, id]);

    try {
      const userRef = doc(db, "users", userId);
      const depRef = doc(db, "deposit_requests", id);

      await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        const depSnap = await transaction.get(depRef);

        if (!depSnap.exists() || depSnap.data().status !== "pending") {
          throw "Already processed";
        }

        const prev = userSnap.data()?.withdrawable || 0;
        const newBal = prev + amount;

        transaction.update(userRef, {
          withdrawable: newBal,
          updatedAt: serverTimestamp(),
        });

        transaction.update(depRef, {
          status: "approved",
          approvedAt: serverTimestamp(),
        });

        const txnRef = doc(collection(userRef, "transactions"));

        transaction.set(txnRef, {
          amount,
          type: "credit",
          status: "completed",
          description: "Wallet Deposit",
          previousBalance: prev,
          newBalance: newBal,
          timestamp: serverTimestamp(),
        });
      });

      await addDoc(
        collection(db, "users", userId, "notifications"),
        {
          title: "Deposit Successful 💰",
          body: `₹${amount} added`,
          seen: false,
          createdAt: serverTimestamp(),
        }
      );

    } catch (e) {
      console.error(e);
    }

    setLoadingIds((prev) => prev.filter((x) => x !== id));
  };

  // ❌ REJECT
  const rejectDeposit = async (d) => {
    const { id, userId, amount } = d;

    setLoadingIds((prev) => [...prev, id]);

    try {
      const depRef = doc(db, "deposit_requests", id);

      await runTransaction(db, async (transaction) => {
        const depSnap = await transaction.get(depRef);

        if (!depSnap.exists() || depSnap.data().status !== "pending") {
          throw "Already processed";
        }

        transaction.update(depRef, {
          status: "rejected",
          rejectedAt: serverTimestamp(),
        });
      });

      await addDoc(
        collection(db, "users", userId, "notifications"),
        {
          title: "Deposit Failed ❌",
          body: `₹${amount} rejected`,
          seen: false,
          createdAt: serverTimestamp(),
        }
      );

    } catch (e) {
      console.error(e);
    }

    setLoadingIds((prev) => prev.filter((x) => x !== id));
  };

  return (
    <div style={container}>
      <h1 style={title}>💰 Deposit Requests</h1>

      {deposits.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No pending deposits</p>
      ) : (
        <div style={grid}>
          {deposits.map((d) => {
            const loading = loadingIds.includes(d.id);

            return (
              <div key={d.id} style={card}>
                
                <p style={user}>👤 {d.userId}</p>
                <h2 style={amount}>₹{d.amount}</h2>

                {d.upiId && (
                  <div style={box}>
                    <span>{d.upiId}</span>
                    <button style={copyBtn} onClick={() => copyText(d.upiId)}>📋</button>
                  </div>
                )}

                {d.utr && (
                  <div style={box}>
                    <span>{d.utr}</span>
                    <button style={copyBtn} onClick={() => copyText(d.utr)}>📋</button>
                  </div>
                )}

                {d.screenshotUrl && (
                  <img
                    src={d.screenshotUrl}
                    alt="proof"
                    style={ssImg}
                    onClick={() => window.open(d.screenshotUrl)}
                  />
                )}

                <div style={btnRow}>
                  <button
                    style={{
                      ...approveBtn,
                      opacity: loading ? 0.6 : 1,
                    }}
                    disabled={loading}
                    onClick={() => approveDeposit(d)}
                  >
                    {loading ? "Processing..." : "Approve"}
                  </button>

                  <button
                    style={{
                      ...rejectBtn,
                      opacity: loading ? 0.6 : 1,
                    }}
                    disabled={loading}
                    onClick={() => rejectDeposit(d)}
                  >
                    {loading ? "Processing..." : "Reject"}
                  </button>
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* 🔥 STYLES */

const container = {
  padding: 20,
  color: "white",
  background: "linear-gradient(135deg, #0F1221, #0A0D1A)",
  minHeight: "100vh",
};

const title = {
  marginBottom: 20,
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
};

const card = {
  background: "#1A1F2E",
  padding: 18,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.05)",
};

const user = {
  fontSize: 13,
  opacity: 0.7,
};

const amount = {
  fontSize: 22,
  margin: "6px 0",
};

const box = {
  display: "flex",
  justifyContent: "space-between",
  background: "#222842",
  padding: 8,
  borderRadius: 8,
  marginBottom: 8,
};

const copyBtn = {
  background: "transparent",
  border: "none",
  color: "#fff",
  cursor: "pointer",
};

const ssImg = {
  width: "100%",
  maxHeight: 160,
  objectFit: "cover",
  borderRadius: 8,
  marginBottom: 10,
  cursor: "pointer",
};

const btnRow = {
  display: "flex",
  gap: 10,
};

const approveBtn = {
  flex: 1,
  background: "#16a34a",
  border: "none",
  padding: 10,
  borderRadius: 8,
  color: "#fff",
  cursor: "pointer",
};

const rejectBtn = {
  flex: 1,
  background: "#dc2626",
  border: "none",
  padding: 10,
  borderRadius: 8,
  color: "#fff",
  cursor: "pointer",
};

export default AdminDeposit;